import { useEffect, useMemo, useState } from 'react';

import BackupApi from '../../api/BackupApi';
import RefreshButton from '../../component/buttons/RefreshButton';
import ConditionalRender from '../../helpers/ConditionalRender';
import BackupHistoryItem from '../../types/backup/BackupHistoryItem';
import BackupAlert from '../../types/backup/BackupAlert';
import BackupSummary from '../../types/backup/BackupSummary';
import BackupChainStatus from './BackupChainStatus';
import BackupHistoryChart from './BackupHistoryChart';
import BackupHistoryTable from './BackupHistoryTable';
import {
    BackupChartMetric,
    BACKUP_CHART_METRICS,
    BACKUP_KIND_COLOURS,
    BACKUP_KIND_LABELS,
    BACKUP_KINDS,
    BACKUP_PERIODS,
    formatInterval,
    typicalIntervals,
} from './backupUtils';

interface Props {
    // The database to show, or null when closed.
    row: BackupSummary | null;
    days: number;
    refreshKey: number;
    onRefresh: () => void;
    onClose: () => void;
}

function BackupHistorySlideout(props: Props) {
    const { row, onClose } = props;
    const id = row?.id;
    const database = row?.database;
    const [days, setDays] = useState(props.days);
    const [metric, setMetric] = useState<BackupChartMetric>('duration');
    const [items, setItems] = useState<BackupHistoryItem[]>([]);
    const [alerts, setAlerts] = useState<BackupAlert[]>([]);
    const [loading, setLoading] = useState(false);

    // Start from the report's period each time a database is opened.
    useEffect(() => {
        setDays(props.days);
    }, [row?.id, row?.database, props.days]);

    useEffect(() => {
        if (id === undefined || database === undefined) {
            setItems([]);
            setAlerts([]);
            return;
        }
        let active = true;
        setLoading(true);
        Promise.all([BackupApi.getHistory(id, database, days), BackupApi.getAlerts(id, database, days)])
            .then(([data, events]) => {
                if (active) {
                    setItems(data);
                    setAlerts(events);
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [id, database, days, props.refreshKey]);

    useEffect(() => {
        if (!row) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [row, onClose]);

    const intervals = useMemo(() => typicalIntervals(items), [items]);
    const show = row !== null;
    const metricLabel = BACKUP_CHART_METRICS.find(m => m.value === metric)?.label;
    const period = BACKUP_PERIODS.find(p => p.days === days)?.label ?? `Last ${days} days`;

    return <div
        className={`offcanvas offcanvas-end backup-history-slideout ${show ? 'show' : ''}`}
        tabIndex={-1}
        role="dialog"
        aria-hidden={!show}
        aria-labelledby="backupHistoryTitle"
        style={{ width: '65vw', visibility: show ? 'visible' : 'hidden' }}
    >
        <div className="offcanvas-body p-0">
            {row && <>
                <div className="backups__slideout-header d-flex align-items-start px-3 pt-3 pb-2">
                    <div className="flex-grow-1">
                        <h5 id="backupHistoryTitle" className="mb-1">
                            <i className="fas fa-database fa-fw me-1" aria-hidden="true"/>{row.database}
                        </h5>
                        <div className="text-muted small">
                            {row.name}
                            {row.recoverymodel && <> &middot; {row.recoverymodel} recovery</>}
                            {row.replicasetname && <> &middot; availability group {row.replicasetname}</>}
                        </div>
                        {row.chainstatus && <div className="small mt-1">
                            Log chain <BackupChainStatus row={row}/>
                            {row.chainmessage && <span className="text-muted ms-1">{row.chainmessage}</span>}
                        </div>}
                    </div>
                    <div className="d-flex align-items-center">
                        <RefreshButton refresh={props.onRefresh} visible/>
                        <div className="btn-group me-2">
                            <button id="btnBackupPeriod" type="button"
                                    className="btn btn-xsm btn-primary dropdown-toggle"
                                    data-bs-toggle="dropdown" aria-expanded="false">
                                {period}
                            </button>
                            <ul className="dropdown-menu border" aria-labelledby="btnBackupPeriod">
                                {BACKUP_PERIODS.map(option => <li key={option.days}>
                                    <span className={`dropdown-item ${option.days === days ? 'active' : ''}`}
                                          role="button" onClick={() => setDays(option.days)}>
                                        {option.label}
                                    </span>
                                </li>)}
                            </ul>
                        </div>
                        <button type="button" className="btn-close custom-slideout-close-button"
                                aria-label="Close" onClick={onClose}/>
                    </div>
                </div>

                <div className="px-3">
                    <div className="card mb-3">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/> Backup history
                            <div className="float-end dropdown">
                                <button id="btnBackupChartMetric" type="button"
                                        className="btn btn-xsm btn-primary dropdown-toggle"
                                        data-bs-toggle="dropdown" aria-expanded="false">
                                    {metricLabel}
                                </button>
                                <ul className="dropdown-menu border" aria-labelledby="btnBackupChartMetric">
                                    {BACKUP_CHART_METRICS.map(option => <li key={option.value}>
                                        <span className={`dropdown-item ${option.value === metric ? 'active' : ''}`}
                                              role="button" onClick={() => setMetric(option.value)}>
                                            {option.label}
                                        </span>
                                    </li>)}
                                </ul>
                            </div>
                        </div>
                        <div className="card-body">
                            <BackupHistoryChart items={items} alerts={alerts} days={days} metric={metric} row={row}/>
                            <ConditionalRender if={Object.keys(intervals).length > 0}>
                                <div className="backups__intervals d-flex flex-wrap justify-content-around small mt-2">
                                    {BACKUP_KINDS.filter(kind => intervals[kind] !== undefined).map(kind =>
                                        <span key={kind}>
                                            <span className="backups__swatch me-1"
                                                  style={{ backgroundColor: BACKUP_KIND_COLOURS[kind] }}/>
                                            <b>{BACKUP_KIND_LABELS[kind]}</b> backups usually run every{' '}
                                            <b>{formatInterval(intervals[kind])}</b>
                                        </span>)}
                                </div>
                            </ConditionalRender>
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body p-0">
                            <BackupHistoryTable items={items} loading={loading}
                                                showReplica={!!row.replicasetname}
                                                row={row}/>
                        </div>
                    </div>
                </div>
            </>}
        </div>
    </div>;
}

export default BackupHistorySlideout;
